/**
 * Delegation RPC Client
 *
 * Typed wrappers for the four server-enforced delegation RPCs
 * defined in migration 197_delegation_policy_v1.sql.
 *
 * The actor is always derived from auth.uid() on the server.
 * No actorId is passed from the client.
 */

import { getSupabaseClient } from './supabaseClient';
import type { ScopeKind } from '../types/prioritization/permissions';

// ============================================================================
// Types
// ============================================================================

interface RpcResult {
  success: boolean;
  error?: string;
}

interface UpdateRolePermissionsResult extends RpcResult {
  count?: number;
}

// Note: assign_process_role was removed (dead, deprecated, zero callers as of
// the cycle-level permission overlay removal). assemble_cycle RPC (migration
// 428_session_assemble_rpcs.sql) is also unreachable from client code — the
// RPC itself is left in place (no migrations in this job); no wrapper exists
// to remove.

// ============================================================================
// elevate_admin
// ============================================================================

export type V1AdminRole = 'WORKSPACE_ADMIN' | 'TENANT_ADMIN';

export async function elevateAdmin(params: {
  targetUserId: string;
  adminRole: V1AdminRole;
  scopeId: string;
}): Promise<RpcResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('elevate_admin', {
    p_target_user_id: params.targetUserId,
    p_admin_role: params.adminRole,
    p_scope_id: params.scopeId,
  });

  if (error) return { success: false, error: error.message };
  return (data as RpcResult) ?? { success: false, error: 'No response from server' };
}

// ============================================================================
// create_role_assignment  (migration 299)
// ============================================================================

interface CreateRoleAssignmentResult extends RpcResult {
  id?: string;
}

export async function createRoleAssignmentRpc(params: {
  subjectType: 'user' | 'group';
  subjectId: string;
  roleId: string;
  /**
   * Any ScopeKind (the server stores text and the scope-bound gate, migration
   * 635, resolves the scope by id), plus 'group'. Org-unit grants use the
   * depth-based kind from lib/roleScope.ts (P0-c, migration 722).
   */
  scopeKind: ScopeKind | 'group';
  scopeId?: string;
  expiresAt?: Date;
  /** Operating-mode tier (migration 556). Defaults to 'supporting'. */
  tier?: 'primary' | 'supporting';
}): Promise<CreateRoleAssignmentResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('create_role_assignment', {
    p_subject_type: params.subjectType,
    p_subject_id: params.subjectId,
    p_role_id: params.roleId,
    p_scope_kind: params.scopeKind,
    p_scope_id: params.scopeId ?? '*',
    p_expires_at: params.expiresAt?.toISOString() ?? null,
    p_tier: params.tier ?? 'supporting',
  });

  if (error) return { success: false, error: error.message };
  return (data as CreateRoleAssignmentResult) ?? { success: false, error: 'No response from server' };
}

// ============================================================================
// delete_role_assignment  (migration 299)
// ============================================================================

export async function deleteRoleAssignmentRpc(assignmentId: string): Promise<RpcResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('delete_role_assignment', {
    p_assignment_id: assignmentId,
  });

  if (error) return { success: false, error: error.message };
  return (data as RpcResult) ?? { success: false, error: 'No response from server' };
}

// ============================================================================
// delete_role_assignments_for_subject  (migration 300)
// ============================================================================

export async function deleteRoleAssignmentsForSubjectRpc(subjectId: string): Promise<RpcResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('delete_role_assignments_for_subject', {
    p_subject_id: subjectId,
  });

  if (error) return { success: false, error: error.message };
  return (data as RpcResult) ?? { success: false, error: 'No response from server' };
}

// ============================================================================
// update_role_permissions
// ============================================================================

export async function updateRolePermissions(params: {
  roleId: string;
  permissions: string[];
}): Promise<UpdateRolePermissionsResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('update_role_permissions', {
    p_role_id: params.roleId,
    p_permissions: params.permissions,
  });

  if (error) return { success: false, error: error.message };
  return (data as UpdateRolePermissionsResult) ?? { success: false, error: 'No response from server' };
}
